import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { useAtom } from 'jotai';
import { cn } from '@localagent/ui/lib/utils';
import { Tooltip } from '@localagent/ui/components/tooltip';
import { useResizablePanel } from '@localagent/ui/hooks/use-resizable-panel';
import { Navigation } from '../Navigation';
import { ChatSessionSidebar } from '../chat/ChatSessionSidebar';
import { NeedsInputIndicator } from '../questions/NeedsInputIndicator';
import { UserNavFooter } from './UserNavFooter';
import { sidebarVisibleAtom, sidebarWidthAtom } from '../../stores/layout/atoms';

const MIN_SIDEBAR_W = 180;
const MAX_SIDEBAR_W = 420;
export const COLLAPSED_SIDEBAR_W = 48;

function SidebarGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M9 3v18" />
    </svg>
  );
}

export type LeftSidebarFrameProps = {
  expanded: boolean;
  onToggle: () => void;
  width: number;
  onResizePointerDown?: (event: ReactPointerEvent<HTMLDivElement>) => void;
  children?: ReactNode;
  footer: ReactNode;
};

export function LeftSidebarFrame({
  expanded,
  onToggle,
  width,
  onResizePointerDown,
  children,
  footer,
}: LeftSidebarFrameProps) {
  const label = expanded ? 'Hide navigation' : 'Show navigation';

  return (
    <aside
      className="group/sidebar relative flex flex-none flex-col border-r bg-muted/30"
      style={{ width }}
      data-testid="left-sidebar"
      data-collapsed={expanded ? 'false' : 'true'}
    >
      <div className="flex h-full min-h-0 flex-col">
        <div
          data-testid="toggle-sidebar-slot"
          className={cn(
            'z-20 flex items-center',
            expanded
              ? 'absolute right-1.5 top-1.5'
              : 'h-9 shrink-0 justify-center',
          )}
        >
          <Tooltip label={label} side="right">
            <button
              type="button"
              data-testid="toggle-sidebar"
              onClick={onToggle}
              aria-pressed={expanded}
              className={cn(
                'rounded-md p-1.5 text-muted-foreground transition-opacity hover:bg-accent hover:text-accent-foreground',
                expanded &&
                  'pointer-events-none opacity-0 group-hover/sidebar:pointer-events-auto group-hover/sidebar:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100',
              )}
            >
              <SidebarGlyph />
            </button>
          </Tooltip>
        </div>
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        {footer}
      </div>
      {expanded && onResizePointerDown ? (
        <div
          data-testid="left-sidebar-resize"
          onPointerDown={onResizePointerDown}
          title="Resize sidebar"
          aria-label="Resize sidebar"
          className="absolute inset-y-0 right-0 z-10 w-1 cursor-col-resize touch-none select-none hover:bg-primary/40 active:bg-primary/60"
        />
      ) : null}
    </aside>
  );
}

export function LeftSidebar() {
  const [expanded, setExpanded] = useAtom(sidebarVisibleAtom);
  const [width, setWidth] = useAtom(sidebarWidthAtom);
  const { size, onHandlePointerDown } = useResizablePanel({
    axis: 'horizontal',
    handleEdge: 'end',
    size: width,
    onCommit: setWidth,
    min: MIN_SIDEBAR_W,
    max: MAX_SIDEBAR_W,
  });

  return (
    <LeftSidebarFrame
      expanded={expanded}
      onToggle={() => setExpanded(!expanded)}
      width={expanded ? size : COLLAPSED_SIDEBAR_W}
      onResizePointerDown={onHandlePointerDown}
      footer={<UserNavFooter compact={!expanded} />}
    >
      <div className="shrink-0 overflow-y-auto">
        <Navigation compact={!expanded} />
      </div>
      <NeedsInputIndicator compact={!expanded} />
      {expanded ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <ChatSessionSidebar />
        </div>
      ) : null}
    </LeftSidebarFrame>
  );
}
